"use client";

import React from "react";
import { LINK_POSITION_BODY, fireLinkClick } from "../../services/analytics";
import { pickDataAttributes } from "../data-attributes";
import { CONTROL_ACTIONS, type ControlAction } from "../control-action.constants";
import { CLOSE_GLYPH_PATH, CLOSE_GLYPH_VIEWBOX } from "../close-glyph";
import styles from "./button.module.css";
import { BUTTON_SIZES, BUTTON_VARIANTS, isToned, type ButtonTonedVariant } from "./button.shared";
import { withSafeRender } from "../safe-render";

/* Re-exported for client-side callers. A SERVER module imports these from
   ./button.shared instead — see that file's header for why. */
export { BUTTON_SIZES, BUTTON_VARIANTS, isToned };
export type { ButtonTonedVariant };

/**
 * Button — renders an <a> when `href` is given and a <button> otherwise.
 * Figma's Active is the RESTING state here, not a pressed one, and the places
 * the code and the design disagree are in design-conflicts/button.mdx.
 *
 * ⚠ The tertiary on-dark set MOVED rather than went away. `10188:16308` is
 * deleted and answers "node not found", which reads exactly like an auth error
 * to whoever opens it, so that id must never be linked again; `12970:18590` is
 * the live set and it publishes the hover and pressed states this paints.
 */
/* Declared here, not in ./button.shared: sibling specs read this union off
   this file's source. button.shared imports it type-only. */
export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "tonal"
  | "specialty"
  | "specialtyPurple"
  | "glass"
  | "ai";
export type ButtonTone = "onDark" | "onLight";
export type ButtonSize = "large" | "medium" | "small" | "xSmall";
/** What activating the button does: navigate / submit, or open the page's iframe
 *  modal. Link shares this vocabulary — defined once in `control-action`. */
export type ButtonAction = ControlAction;

/* The published button actions — the shared set under the button's own name, so
   a CMS string can never reach the DOM as a bogus value (anything off it falls
   back to `redirect`). Not JSDoc: Storybook's docgen reads a documented export
   as a component and adds keys to the object, which here is the shared set. */
export const BUTTON_ACTIONS = CONTROL_ACTIONS;

interface ButtonBaseProps {
  size?: ButtonSize;
  /**
   * Renders an anchor instead of a button. An empty string takes the <button>
   * branch deliberately: `href=""` on a real anchor resolves to the CURRENT page.
   */
  href?: string;
  /** Only meaningful for the <button> form. */
  type?: "button" | "submit";
  /**
   * The submitter's own value, for the `<button>` form only — it sets a
   * dialog's `returnValue`. Not a form-data field; there is no `name` beside it.
   */
  value?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  /** Decorative mark before the wording, hidden from assistive tech. Ignored on `ai`. */
  startIcon?: React.ReactNode;
  /** Decorative mark only — it is hidden from assistive tech. */
  endIcon?: React.ReactNode;
  /** Marks the current page. Ignored on the <button> form — only a destination can claim it. */
  current?: boolean;
  /**
   * Opts into the engaged appearance `glass` and `ai` carry, announced as
   * aria-pressed on the <button> form and ignored on a link. Leave it undefined
   * for an ordinary button — `false` announces a toggle in its off state.
   */
  pressed?: boolean;
  /**
   * Runs after the click has been reported, and not at all when `disabled`. The
   * event is passed through because intercepting a link navigation means
   * calling `preventDefault()` on it.
   */
  onClick?: (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => void;
  /** DOM `id`, rendered on whichever element the button becomes. */
  id?: string;
  /**
   * What activating the button does. `redirect` (the default) follows `href` /
   * submits the form. `iframe_modal` makes it a trigger for the page's iframe
   * modal: it renders `data-iframe-modal`, so the page's `IframeModal` opens. On
   * the `<a>` form (an `href` is set) the embedded url is that href, which stays
   * the no-JS fallback; on the `<button>` form (no `href`) there is no href to
   * embed, so name the url on `data-iframe-src` via `dataAttributes`. Needs an
   * `IframeModal` on the page; see `organisms/iframe-modal`. `legal_modal` does
   * the same for the page's `LegalModal`, rendering `data-legal-modal` — ⚠ that
   * one FETCHES the href and renders the document, so it works on the `<a>` form
   * ONLY; a `<button>` has no document to open. A disabled button never
   * triggers. Anything off the set falls back to `redirect`.
   */
  buttonAction?: ButtonAction;
  /**
   * Declarative `data-*` hooks to render on the element — e.g.
   * `{ "data-iframe-src": "/x" }` to embed a url other than `href` when
   * `buttonAction="iframe_modal"` is on the `<button>` form. ⚠ `data-*` keys only;
   * a `className`, handler or `aria-*` passed here is dropped, and it cannot
   * overwrite the button's own reporting attributes or the `data-iframe-modal`
   * that `buttonAction="iframe_modal"` sets.
   */
  dataAttributes?: Record<string, string>;
  className?: string;
}

/**
 * What the click reports. Flat fields rather than one `analytics` object,
 * because they are the CMS contract. The container reports a click only on an
 * element — or its nearest ancestor — carrying `data-linkposition` /
 * `data-linkdestination`; a bare anchor reports nothing. Button pushes its
 * own report only when given `linkName`, which is why `linkName` and
 * `linkAttributes` exclude each other.
 */
export type ButtonAnalyticsProps = {
  /** Where on the page the button sits. The push defaults it to body; the `linkAttributes` attribute is omitted when empty. */
  linkPosition?: string;
  /** Where the click leads, defaulting to `href`: feeds the `linkName` push on either form, and the attribute only on a live link with `linkAttributes`. */
  linkDestination?: string;
} & (
  | {
      /** Names Button's own push — the only report unless an attributed ancestor covers the click too, when it is reported twice. */
      linkName?: string;
      linkAttributes?: false;
    }
  | {
      /** Not accepted here: the container names the click and ignores `data-linkname`. */
      linkName?: never;
      /** Writes `data-linkposition` on both forms and `data-linkdestination` on a live link, for the container, and pushes nothing. A disabled control gets neither. Absent, no attributes. */
      linkAttributes: true;
    }
);

/**
 * `tone` is paired with `variant`: only `tertiary`, `ai` and `tonal` have a
 * second surface. The first member stays wide so a variant row carrying no
 * tone still typechecks. Asserted at runtime too, since the type never sees
 * CMS data.
 */
type ButtonToneProps =
  | { variant?: ButtonVariant; tone?: never }
  | {
      variant: ButtonTonedVariant;
      /**
       * Which surface the button sits on. tertiary recolours its label, ai
       * repaints its fill, tonal flips to the slate-950 fill Figma publishes
       * as "Tonal button-onLight" (12346:13465) — see design-conflicts/button.mdx.
       */
      tone?: ButtonTone;
    };

/**
 * An icon-only button must be named, so with no `children` an `ariaLabel` is
 * required. ⚠ That catches a MISSING property only — `strictNullChecks` is off
 * in this repo, so present-and-undefined compiles clean and announcesNothing
 * below is the real guard.
 */
export type ButtonProps = ButtonBaseProps &
  ButtonAnalyticsProps &
  ButtonToneProps &
  (
    | {
        children: React.ReactNode;
        /** Read aloud instead of the wording, when the wording is not descriptive. */
        ariaLabel?: string;
      }
    | {
        children?: never;
        /** Required: with no wording, this is the button's only accessible name. */
        ariaLabel: string;
      }
  );

/**
 * The `ai` variant's own mark, part of the variant rather than something a call
 * site supplies — see startMark below. 24 x 22.642, NOT square: the height is
 * the aspect ratio, not the icon box. The gradient id is document-global, so
 * useId keeps two ai buttons on one page from emitting it twice, and React's
 * colons read badly inside url() so they are cut.
 */
function AiSparkle() {
  const gradientId = `dds-ai-sparkle-${React.useId().replace(/:/g, "")}`;

  return (
    <svg className={styles.aiIcon} viewBox="0 0 24.0003 22.6419" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M15.2481 3.24618C15.9859 3.25596 16.6232 3.78779 16.7999 4.53915L17.9513 9.45419C17.9695 9.48144 18.0049 9.5187 18.0304 9.53817L22.8087 10.9141C23.5208 11.1186 24.0109 11.8017 24.0001 12.5743V12.5763C23.991 13.351 23.4826 14.0192 22.7667 14.2022L17.961 15.4337C17.9319 15.4551 17.8933 15.4963 17.8751 15.5235L16.5977 20.3956C16.4028 21.1392 15.7506 21.6534 15.0128 21.6417C14.2752 21.6318 13.6378 21.1008 13.461 20.3497L12.3097 15.4337C12.2915 15.4066 12.2569 15.3701 12.2315 15.3507L7.45224 13.9737C6.74021 13.7691 6.2499 13.0862 6.26083 12.3136C6.26999 11.539 6.77852 10.8717 7.49423 10.6886L12.3009 9.45614C12.33 9.43468 12.3677 9.39347 12.3858 9.3663L13.6632 4.49422C13.8599 3.74867 14.5103 3.2345 15.2481 3.24618ZM4.75888 8.41614e-05C5.15055 0.00542909 5.48691 0.25679 5.58114 0.609459L6.19052 2.92196C6.19974 2.93597 6.2186 2.95321 6.23153 2.962L8.76083 3.60946C9.13779 3.70597 9.39721 4.02747 9.39169 4.39071C9.38615 4.7557 9.11617 5.07032 8.73739 5.15633L6.19247 5.73641C6.17784 5.74683 6.15789 5.76611 6.14852 5.7784L5.47177 8.0704C5.36828 8.42137 5.02465 8.6616 4.6329 8.65633C4.24121 8.65101 3.90303 8.40061 3.81063 8.04794L3.20126 5.73544C3.19202 5.7214 3.17318 5.70319 3.16024 5.69442L0.630947 5.04696C0.253975 4.95044 -0.00545654 4.63072 8.71567e-05 4.26571C0.00583808 3.90092 0.275776 3.58705 0.654384 3.50106L3.19931 2.92001C3.21405 2.90945 3.23404 2.89023 3.24325 2.87801L3.92001 0.586022C4.02358 0.236933 4.36906 -0.00517922 4.75888 8.41614e-05Z"
        fill={`url(#${gradientId})`}
      />
      <defs>
        <radialGradient
          cx="0"
          cy="0"
          gradientTransform="matrix(-57.3913 23.2647 -173.817 -54.3182 21.3913 -2.11952e-06)"
          gradientUnits="userSpaceOnUse"
          id={gradientId}
          r="1"
        >
          <stop offset="0.0202295" stopColor="#ffffff" />
          <stop offset="0.113736" stopColor="#09f0ff" />
          <stop offset="0.422029" stopColor="#006dff" />
          <stop offset="0.732507" stopColor="#000499" />
          <stop offset="0.965402" stopColor="#000443" />
        </radialGradient>
      </defs>
    </svg>
  );
}

/**
 * The glyph the Modal's close button draws, which is this component at
 * variant="tertiary" size="small" with no wording. `currentColor` rather than a
 * fixed paint, so the glyph follows the button into `disabled` and
 * `tone="onLight"` instead of staying bright on a greyed one.
 */
function CloseIconImpl() {
  return (
    <svg
      aria-hidden="true"
      height="100%"
      viewBox={CLOSE_GLYPH_VIEWBOX}
      width="100%"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d={CLOSE_GLYPH_PATH}
        fill="currentColor"
      />
    </svg>
  );
}
export const CloseIcon = withSafeRender(CloseIconImpl, "CloseIcon");

/**
 * True when nothing in `children` can announce anything — dev diagnostic only,
 * never the geometry. It asks whether a label will be HEARD where `iconOnly`
 * asks whether one was SUPPLIED: `[]`, `[null]` and `" "` all satisfy the
 * labelled member of ButtonProps and then render an empty span. A number counts
 * as wording. Only a child's OWN props are readable, so `CloseIcon` is named
 * here because this file declares it.
 */
function announcesNothing(children: React.ReactNode): boolean {
  return React.Children.toArray(children).every(child => {
    if (typeof child === "string" || typeof child === "number") {
      return String(child).trim() === "";
    }
    if (!React.isValidElement(child)) {
      return false;
    }

    const props = child.props as {
      "aria-hidden"?: boolean | string;
      children?: React.ReactNode;
    };

    if (child.type === React.Fragment) {
      return announcesNothing(props.children);
    }

    if (child.type === CloseIcon) {
      return true;
    }

    // A bare `aria-hidden` arrives as the boolean; JSX and an adapter both
    // spell the attribute form as the string.
    return props["aria-hidden"] === true || props["aria-hidden"] === "true";
  });
}

/**
 * The value if it is one of the closed set, the default if it is not.
 * The SET names the result type: inferring it from `fallback` too wins on
 * priority and collapses it to that literal, making the comparisons below TS2367.
 */
function published<S extends Record<string, true>>(
  set: S,
  value: string,
  fallback: Extract<keyof S, string>
): Extract<keyof S, string> {
  return Object.prototype.hasOwnProperty.call(set, value) ?
    (value as Extract<keyof S, string>) :
    fallback;
}

function ButtonImpl({
  variant = "primary",
  tone = "onDark",
  size = "large",
  href,
  type = "button",
  value,
  disabled = false,
  fullWidth = false,
  startIcon,
  endIcon,
  ariaLabel,
  current = false,
  pressed,
  onClick,
  id,
  buttonAction = "redirect",
  dataAttributes,
  className,
  children,
  linkName,
  linkPosition,
  linkDestination,
  linkAttributes = false
}: ButtonProps) {
  /* One predicate drives both the square geometry and the label element, so the
     two cannot disagree. A plain `!children` does not work: `children={0}` is
     falsy while React still paints a visible "0". */
  const hasLabel = !(
    children === undefined ||
    children === null ||
    children === false ||
    children === ""
  );
  const iconOnly = !hasLabel;

  /* CMS data never passes through TypeScript, and wording that is present and
     empty, or that is really a mark, satisfies the labelled member either way. */
  if (process.env.NODE_ENV !== "production" && !ariaLabel && announcesNothing(children)) {
    console.error(
      "[Button] renders no wording and has no ariaLabel, so it has no accessible name. Give it wording, or an ariaLabel describing what it does."
    );
  }

  /* An unfilled Storyblok link field arrives as `""` and takes the <button>
     branch, so a link authored in the CMS renders as a button going nowhere. */
  if (process.env.NODE_ENV !== "production" && href === "") {
    console.error(
      "[Button] has an empty href, so it renders a form button rather than a link and goes nowhere. Fill in the destination, or leave href out."
    );
  }

  /* Every later branch keys off these two rather than off `variant` and `size`,
     or the guard would hold for the class and leak everywhere else. */
  const resolvedVariant = published(BUTTON_VARIANTS, variant, "primary");
  const resolvedSize = published(BUTTON_SIZES, size, "large");

  /* Only the <button> form can announce a pressed state, so only it may paint one. */
  const showPressed = pressed && !href;

  /* Reporting goes first because the call site's handler may navigate away and
     an event raised after that is lost. The disabled guard is for the LINK: a
     disabled link is a real anchor that has merely lost its href, so it still
     receives clicks, where a disabled <button> never dispatches one. */
  const handleClick = (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => {
    if (disabled) {
      return;
    }

    /* The container reports a click only on an element — or its nearest
       ancestor — carrying these attributes; a push here on top of them would
       count the click twice. */
    if (linkName && !linkAttributes) {
      fireLinkClick({
        linkDestination: linkDestination || href,
        linkName,
        linkPosition: linkPosition || LINK_POSITION_BODY
      });
    }

    onClick?.(event);
  };

  /* The pairing ButtonToneProps states, asserted where the type cannot reach. */
  const toned = isToned(resolvedVariant);

  /* Also drives the aria-pressed line below, so paint and announcement stay in step. */
  const paintsEngaged = resolvedVariant === "glass" || resolvedVariant === "ai";

  const classes = [
    styles.button,
    styles[resolvedVariant],
    styles[resolvedSize],
    toned && tone === "onLight" ? styles.onLight : null,
    iconOnly ? styles.iconOnly : null,
    showPressed ? styles.pressed : null,
    fullWidth ? styles.fullWidth : null,
    className
  ]
    .filter(Boolean)
    .join(" ");

  /* One leading slot, and on ai the sparkle owns it — a caller's `startIcon` is
     dropped rather than drawn beside it. Runtime, not the type: these props
     come from Storyblok. */
  const startMark = resolvedVariant === "ai" ? <AiSparkle /> : startIcon;

  const content = (
    <>
      {startMark && (
        <span aria-hidden="true" className={styles.icon}>
          {startMark}
        </span>
      )}
      {hasLabel && <span className={styles.label}>{children}</span>}
      {endIcon && (
        <span aria-hidden="true" className={styles.icon}>
          {endIcon}
        </span>
      )}
    </>
  );

  /* One value for the anchor and the reported destination, so the report
     cannot name a destination the anchor does not carry. */
  const renderedHref = disabled ? undefined : href;

  /* Absent rather than empty when unset: GTM reports "" as a value. A
     disabled control reports nothing, so it carries neither attribute. */
  const tracked = linkAttributes && !disabled;
  const trackedPosition = tracked ? linkPosition || undefined : undefined;
  const trackedDestination = tracked ? linkDestination || renderedHref || undefined : undefined;

  /* `iframe_modal` renders the trigger attribute the page's IframeModal listens
     for — the <a> form embeds `href`, the <button> form names the url on
     `data-iframe-src`. Validated against the set (bogus CMS string → redirect),
     off when disabled, and GATED on a real embed source: with neither `href` nor
     `data-iframe-src` a <button> would carry an inert trigger the listener drops.
     Merged LAST so a caller cannot strip it with `undefined`; `dataProps` is still
     spread FIRST on each element, so href, id and the data-link* pair win. */
  const resolvedAction = published(BUTTON_ACTIONS, buttonAction, "redirect");
  const opensIframeModal =
    resolvedAction === "iframe_modal" &&
    !disabled &&
    (Boolean(href) || dataAttributes?.["data-iframe-src"] !== undefined);
  /* ⚠ The LegalModal listens on `a[data-legal-modal]` and fetches the href, so
     only the <a> form can carry it — a <button> has no document to open. */
  const opensLegalModal = resolvedAction === "legal_modal" && !disabled && Boolean(href);
  const dataProps = pickDataAttributes({
    ...dataAttributes,
    ...(opensIframeModal ? { "data-iframe-modal": "" } : {}),
    ...(opensLegalModal ? { "data-legal-modal": "" } : {})
  });

  if (href) {
    return (
      /* `pressed` is ignored on this form, appearance included. aria-pressed on
         a link is invalid and aria-current is owned by `current`, so painting
         the state with nothing to announce it would be state by colour alone. */
      <a
        {...dataProps}
        aria-current={current ? "page" : undefined}
        // A disabled link is not a thing in HTML: dropping href makes it
        // unfollowable while role and aria-disabled keep it announced.
        aria-disabled={disabled || undefined}
        aria-label={ariaLabel}
        className={classes}
        data-linkdestination={trackedDestination}
        data-linkposition={trackedPosition}
        href={renderedHref}
        id={id}
        onClick={handleClick}
        role={disabled ? "link" : undefined}
        /* Dropping href takes the anchor out of the tab order and role="link" does
           not put it back, so without this the state is announced to nobody. */
        tabIndex={disabled ? 0 : undefined}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      {...dataProps}
      aria-label={ariaLabel}
      /* Asserted wherever the stylesheet paints an engaged state. Passed
         through rather than coerced: undefined leaves the attribute off, where
         an explicit false announces a toggle that is off. */
      aria-pressed={paintsEngaged ? pressed : undefined}
      className={classes}
      data-linkposition={trackedPosition}
      disabled={disabled}
      id={id}
      onClick={handleClick}
      type={type === "submit" ? "submit" : "button"}
      /* Omitted when not given rather than sent as "": an empty submitter value
         is indistinguishable from a dialog closed by Escape. */
      value={value}
    >
      {content}
    </button>
  );
}
export const Button = withSafeRender(ButtonImpl, "Button");

export default Button;
